import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { CollapsibleSection, CollapsibleSectionsPanel } from "../components/CollapsibleSections";
import DocumentTakeover from "../design/DocumentTakeover";
import StatusChip, { StatusTone } from "../components/StatusChip";
import CodeStep from "./CodeStep";
import * as api from "./signApi";

type Phase = "loading" | "code" | "list" | "doc" | "done" | "error";

/** The API refuses a longer reason (DeclineIn, signatures/schemas.py). */
const DECLINE_REASON_MAX = 1000;
const GENERIC_TITLE = "Documents to sign";
const LINK_LOCKED_MESSAGE = "Too many wrong codes. Ask the sender to send a new link.";

/** The signer's step on a document, as a pill. */
export function documentState(state: string, signing: boolean): { label: string; tone: StatusTone } {
  switch (state) {
    case "Open":
      return { label: signing ? "To sign" : "To approve", tone: "teal" };
    case "Signed":
      return { label: "Signed", tone: "green" };
    case "Approved":
      return { label: "Approved", tone: "green" };
    case "Declined":
      return { label: "Declined", tone: "red" };
    case "Voided":
      return { label: "Cancelled", tone: "neutral" };
    default:
      return { label: "Not yet open", tone: "neutral" };
  }
}

/** Every open document has been opened, so "Sign all" / "Approve all" may run (the server checks it too). */
export function allOpenReviewed(documents: api.BatchDocument[]): boolean {
  const open = documents.filter((d) => d.state === "Open");
  return open.length > 0 && open.every((d) => d.reviewed);
}

/**
 * A batch link (version-43; Aaron 2026-10-19 "One link, one code, sign each"): one link and, for Contract Sign, one
 * code for several documents. The signer opens each document (the shared DocumentTakeover viewer) and signs or
 * approves it, or does them all once each has been opened. Declining (reason required) applies to one document;
 * the others carry on. Rendered by SignPage when the single view answers USE_BATCH_LINK.
 */
const BatchSignPage: React.FC<{ token: string }> = ({ token }) => {
  const [info, setInfo] = useState<api.BatchView | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [current, setCurrent] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [accept, setAccept] = useState(false);
  const [comment, setComment] = useState("");
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneText, setDoneText] = useState("");
  const viewSeq = useRef(0);

  const signing = info?.kind === "contract_sign";

  const fail = useCallback((e: any) => {
    if (e?.code === "LINK_LOCKED") {
      viewSeq.current++;
      api.clearSession();
      setError(LINK_LOCKED_MESSAGE);
      setPhase("error");
      return;
    }
    setError(e?.message ?? "Something went wrong.");
  }, []);

  const toCodeStep = () => {
    api.clearSession();
    setCodeSent(false);
    setCode("");
    setFile(null);
    setCurrent(null);
    setPhase("code");
    setError("For your security your code has timed out. Email yourself a new code to carry on.");
  };

  const onActionError = (e: any) => {
    if (e?.code === "OTP_REQUIRED" && signing) return toCodeStep();
    if (e?.code === "LINK_CLOSED") {
      setDoneText("Every document in this set has been dealt with. You can close this tab.");
      setPhase("done");
      return;
    }
    fail(e);
  };

  /** Read the view again; when nothing is left for this signer, finish. */
  const refresh = async (finished?: string): Promise<api.BatchView | null> => {
    const seq = ++viewSeq.current;
    try {
      const v = await api.batchView(token);
      if (seq !== viewSeq.current) return null;
      setInfo(v);
      return v;
    } catch (e: any) {
      if (seq !== viewSeq.current) return null;
      if (e?.code === "LINK_CLOSED" && finished) {
        setDoneText(finished);
        setPhase("done");
        return null;
      }
      throw e;
    }
  };

  useEffect(() => {
    const seq = ++viewSeq.current;
    api
      .batchView(token)
      .then((v) => {
        if (seq !== viewSeq.current) return;
        if (v.locked === true) {
          fail({ code: "LINK_LOCKED" });
          return;
        }
        setInfo(v);
        setPhase(v.needs_otp && !v.verified ? "code" : "list");
      })
      .catch((e) => {
        if (seq !== viewSeq.current) return;
        fail(e);
        setPhase("error");
      });
  }, [token, fail]);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      onActionError(e);
    } finally {
      setBusy(false);
    }
  };

  const openDocument = (n: number) =>
    run(async () => {
      const doc = info?.documents.find((d) => d.n === n);
      const f = await api.batchPdfFile(token, n, doc?.title ?? null);
      setFile(f);
      setCurrent(n);
      setDeclining(false);
      setReason("");
      setPhase("doc");
      await refresh(); // the document is now reviewed
    });

  const finishedText = signing
    ? "You have dealt with every document. The executed copies will be emailed to you once every party has signed."
    : "You have dealt with every document. Thank you.";

  const afterDecision = async () => {
    const v = await refresh(finishedText);
    if (!v) return;
    if (!v.documents.some((d) => d.state === "Open")) {
      setDoneText(finishedText);
      setPhase("done");
      return;
    }
    setFile(null);
    setCurrent(null);
    setPhase("list");
  };

  const signBody = () => ({ typed_name: name.trim(), typed_title: title.trim(), accept });
  const decide = (n: number | null) =>
    run(async () => {
      if (signing) await api.batchSign(token, n, signBody());
      else await api.batchApprove(token, n, comment);
      await afterDecision();
    });
  const decline = (n: number) =>
    run(async () => {
      await api.batchDecline(token, n, reason.trim());
      setDeclining(false);
      setReason("");
      await afterDecision();
    });

  if (phase === "error") {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (phase === "loading" || !info) {
    return (
      <Box p={4}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }
  if (phase === "done") {
    return (
      <Box p={4} maxWidth={720} mx="auto">
        <Typography variant="h5" component="h1" gutterBottom>
          {info.title || GENERIC_TITLE}
        </Typography>
        <Alert severity="success">{doneText}</Alert>
      </Box>
    );
  }
  if (phase === "code") {
    return (
      <CodeStep
        title={info.title || GENERIC_TITLE}
        signerName={info.signer_name}
        error={error}
        busy={busy}
        codeSent={codeSent}
        code={code}
        onCodeChange={setCode}
        prompt={`To confirm it is you, we will email you a 6-digit code. One code covers all ${info.documents.length} documents.`}
        onSend={() =>
          run(async () => {
            await api.batchSendOtp(token);
            setCodeSent(true);
          })
        }
        onContinue={() =>
          run(async () => {
            await api.batchVerifyOtp(token, code);
            const v = await refresh();
            if (v) setPhase("list");
          })
        }
      />
    );
  }

  const verb = signing ? "Sign" : "Approve";
  const openCount = info.documents.filter((d) => d.state === "Open").length;
  // The acceptance names exactly what the button signs: this document, or every open one (Pierre, 2026-10-21, did
  // not see that each document is signed on its own).
  const acceptLabel = (all: boolean) =>
    all
      ? `I accept all ${openCount} open documents and agree to sign them electronically`
      : "I accept this document and agree to sign it electronically";
  const signerFields = (all: boolean) => (
    <>
      {signing ? (
        <>
          <TextField label="Your full name" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Your title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <FormControlLabel
            control={<Checkbox checked={accept} onChange={(e) => setAccept(e.target.checked)} />}
            label={acceptLabel(all)}
          />
        </>
      ) : (
        <TextField
          label="Comment (optional)"
          multiline
          minRows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      )}
    </>
  );
  const ready = !signing || (accept && !!name.trim());

  if (phase === "doc" && current !== null) {
    const doc = info.documents.find((d) => d.n === current);
    const open = doc?.state === "Open";
    return (
      <DocumentTakeover
        file={file}
        fileName={file?.name ?? null}
        title={doc?.title || `Document ${current}`}
        backLabel="All documents"
        enableSaveClose={false}
        onBack={() => {
          setFile(null);
          setCurrent(null);
          setPhase("list");
        }}
        busy={busy}
        rail={{
          stages: [
            { key: "review", label: "Review" },
            { key: "decide", label: verb },
          ],
          stage: 0,
          phase: "running",
          title: `Document ${current} of ${info.documents.length}`,
          note: info.signer_name ? `For ${info.signer_name}` : "",
        }}
        notices={error ? [{ key: "error", severity: "error", text: error, onDismiss: () => setError(null) }] : []}
      >
        <Stack spacing={2} p={2}>
          {!open ? (
            <Alert severity="info">
              This document is {documentState(doc?.state ?? "", signing).label.toLowerCase()}.
            </Alert>
          ) : declining ? (
            <>
              <TextField
                label="Reason for declining this document"
                required
                autoFocus
                multiline
                minRows={3}
                value={reason}
                inputProps={{ maxLength: DECLINE_REASON_MAX }}
                helperText={`The sender will see this. The other documents are not affected. ${reason.length}/${DECLINE_REASON_MAX}`}
                onChange={(e) => setReason(e.target.value)}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  disabled={busy || !reason.trim()}
                  onClick={() => decline(current)}
                >
                  Confirm decline
                </Button>
                <Button disabled={busy} onClick={() => setDeclining(false)}>
                  Cancel
                </Button>
              </Stack>
            </>
          ) : (
            <>
              {signerFields(false)}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy || !ready} onClick={() => decide(current)}>
                  {verb} this document
                </Button>
                <Button disabled={busy} onClick={() => setDeclining(true)}>
                  Decline
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Each document is {verb === "Sign" ? "signed" : "approved"} on its own. Use &ldquo;All documents&rdquo;
                (top left) to see the list. If this one needs a change, Decline it with a reason; the others carry on.
              </Typography>
            </>
          )}
        </Stack>
      </DocumentTakeover>
    );
  }

  const reviewedAll = allOpenReviewed(info.documents);
  return (
    <Box p={{ xs: 2, md: 4 }} maxWidth={720} mx="auto">
      <Typography variant="h5" component="h1" gutterBottom>
        {info.title || GENERIC_TITLE}
      </Typography>
      {info.signer_name && (
        <Typography color="text.secondary" gutterBottom>
          For {info.signer_name}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      <Typography sx={{ my: 2 }}>
        Each document is {verb === "Sign" ? "signed" : "approved"} on its own: open it, read it, then{" "}
        {verb.toLowerCase()} it there. If one needs a change, open it and choose Decline with a reason; the others carry
        on. Once you have opened every document, you can also {verb.toLowerCase()} the rest together below.
      </Typography>
      <CollapsibleSectionsPanel inset="sheet" testId="batch-documents">
        <CollapsibleSection title="Documents" count={info.documents.length} body="rows">
          <Stack divider={<Divider flexItem />}>
            {info.documents.map((d) => {
              const pill = documentState(d.state, signing);
              return (
                <Stack
                  key={d.n}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  py={1.5}
                  data-testid={`batch-document-${d.n}`}
                >
                  <Typography sx={{ flexGrow: 1 }}>
                    {d.n}. {d.title || `Document ${d.n}`}
                  </Typography>
                  {d.state === "Open" && (
                    <Typography variant="body2" color="text.secondary">
                      {d.reviewed ? "Opened" : "Not opened yet"}
                    </Typography>
                  )}
                  <StatusChip label={pill.label} tone={pill.tone} />
                  <Button disabled={busy} onClick={() => openDocument(d.n)}>
                    {d.state === "Open" ? `Open to ${verb.toLowerCase()}` : "View"}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </CollapsibleSection>
      </CollapsibleSectionsPanel>
      {openCount > 0 && (
        <Stack spacing={2} mt={3}>
          {signerFields(true)}
          <Box>
            <Button variant="contained" disabled={busy || !ready || !reviewedAll} onClick={() => decide(null)}>
              {verb} all {openCount}
            </Button>
            {!reviewedAll && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Open each document first; {verb.toLowerCase()} all is available once you have opened every one.
              </Typography>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default BatchSignPage;
